// react
import {
  cloneElement,
  isValidElement,
  ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

// components
import { Box, Popover as MUIPopover, Portal } from '@mui/material';

// styles
import { StyledBackdrop, TriggerContainer } from './Popover.styles';

// types
import { PopoverProps } from './Popover.types';
import { rightClickThroughBackdrops } from './Popover.utils';

const Popover = ({
  anchorOrigin,
  anchorPosition,
  children,
  childRef,
  clickEvent = 'onClick',
  component,
  id,
  open = false,
  useForcedState = false,
  forceOpen = false,
  forceComponentWidth = false,
  showBackdrop = true,
  useTransition = true,
  onClose,
  onExited,
  onMount,
  onOpen
}: PopoverProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(open);
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined);

  const isOpen = isPopoverOpen || forceOpen;

  const anchorRef = useRef<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();

    if (!useForcedState) {
      setIsPopoverOpen(!isPopoverOpen);
    }
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
    onClose?.();
  };

  const anchorElement = isValidElement(children)
    ? cloneElement(
        children as ReactElement<{ ref?: (node: HTMLElement | null) => void }>,
        {
          ref: (node: HTMLElement | null) => (anchorRef.current = node)
        }
      )
    : children;

  const componentElement = isValidElement(component)
    ? cloneElement(component as ReactElement<{ style?: React.CSSProperties }>, {
        style: { ...(forceComponentWidth && { width: anchorWidth ?? 'auto' }) }
      })
    : component;

  useLayoutEffect(() => {
    if (forceComponentWidth && anchorRef.current) {
      const { width } = anchorRef.current.getBoundingClientRect();
      setAnchorWidth(width);
    }
  }, [anchorRef.current, isPopoverOpen]);

  useEffect(() => {
    onMount?.();
  }, [anchorElement]);

  useEffect(() => {
    if (!open) setIsPopoverOpen(false);
  }, [open]);

  useEffect(() => {
    if (isOpen) onOpen?.();
  }, [isOpen]);

  return (
    <>
      <TriggerContainer isOpen={isOpen}>
        <MUIPopover
          {...(!useTransition && { transitionDuration: 0 })}
          id={id}
          anchorReference={!!anchorPosition ? 'anchorPosition' : 'anchorEl'}
          anchorPosition={anchorPosition}
          anchorEl={childRef?.current || anchorRef.current}
          anchorOrigin={anchorOrigin}
          closeAfterTransition
          disableRestoreFocus
          onClose={handleClose}
          open={isOpen}
          slotProps={{
            transition: { onExited },
            paper: {
              style: {
                width: anchorWidth ?? 'auto',
                overflow: 'unset',
                boxShadow: 'none',
                backgroundColor: 'transparent'
              }
            },
            backdrop: {
              onContextMenu: (ev) => rightClickThroughBackdrops(ev, handleClose)
            }
          }}
        >
          {componentElement}
        </MUIPopover>

        <Box display="contents" {...{ [clickEvent]: handleClick }}>
          {anchorElement}
        </Box>
      </TriggerContainer>

      {showBackdrop && (
        <Portal>
          <StyledBackdrop open={isOpen} />
        </Portal>
      )}
    </>
  );
};
export default Popover;
